import { readonly, ref, type Ref } from 'vue'
import { uid } from '../utils/dom'

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface Toast {
  id: string
  variant: ToastVariant
  title: string
  /** Optional second line. Keep it short — a toast is not an error report. */
  description?: string
  /** Milliseconds before auto-dismiss. `0` pins it until dismissed by hand. */
  duration: number
  action?: ToastAction
  /** Set for toasts raised from an ApiError, so support can quote it. */
  requestId?: string
}

export type ToastInput = Partial<Omit<Toast, 'id'>> & { title: string }

/**
 * Module-level queue.
 *
 * A toast is raised from anywhere — an API error handler, a store action, a
 * router guard — none of which have a component instance to inject from. So
 * the queue is a singleton and `<UiToastHost>` is a view onto it, mounted once
 * at the app root.
 */
const toasts = ref<Toast[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()

/** Cap the visible stack. Beyond this the oldest is dropped, not queued. */
const MAX_VISIBLE = 4

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  info: 5000,
  success: 4000,
  warning: 8000,
  // A failure stays until acknowledged. Auto-dismissing an error the user did
  // not read is how a failed payout run gets treated as a successful one.
  danger: 0,
}

function dismiss(id: string): void {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
  toasts.value = toasts.value.filter((toast) => toast.id !== id)
}

function push(input: ToastInput): string {
  const variant = input.variant ?? 'info'
  const toast: Toast = {
    id: uid('toast'),
    variant,
    title: input.title,
    duration: input.duration ?? DEFAULT_DURATION[variant],
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.action !== undefined ? { action: input.action } : {}),
    ...(input.requestId !== undefined ? { requestId: input.requestId } : {}),
  }

  const next = [...toasts.value, toast]
  while (next.length > MAX_VISIBLE) {
    const dropped = next.shift()
    if (dropped) {
      const timer = timers.get(dropped.id)
      if (timer) clearTimeout(timer)
      timers.delete(dropped.id)
    }
  }
  toasts.value = next

  if (toast.duration > 0) {
    timers.set(
      toast.id,
      setTimeout(() => dismiss(toast.id), toast.duration),
    )
  }
  return toast.id
}

function clear(): void {
  for (const timer of timers.values()) clearTimeout(timer)
  timers.clear()
  toasts.value = []
}

/** Suspends the auto-dismiss timer — used while a toast is hovered or focused. */
function pause(id: string): void {
  const timer = timers.get(id)
  if (timer) {
    clearTimeout(timer)
    timers.delete(id)
  }
}

/** Restarts the auto-dismiss timer after a `pause`. */
function resume(id: string): void {
  const toast = toasts.value.find((candidate) => candidate.id === id)
  if (!toast || toast.duration <= 0 || timers.has(id)) return
  timers.set(
    id,
    setTimeout(() => dismiss(id), toast.duration),
  )
}

export interface UseToastReturn {
  toasts: Readonly<Ref<readonly Toast[]>>
  push: (input: ToastInput) => string
  info: (title: string, description?: string) => string
  success: (title: string, description?: string) => string
  warning: (title: string, description?: string) => string
  danger: (title: string, description?: string) => string
  dismiss: (id: string) => void
  clear: () => void
  pause: (id: string) => void
  resume: (id: string) => void
}

export function useToast(): UseToastReturn {
  const shorthand =
    (variant: ToastVariant) =>
    (title: string, description?: string): string =>
      push(description === undefined ? { title, variant } : { title, description, variant })

  return {
    toasts: readonly(toasts) as Readonly<Ref<readonly Toast[]>>,
    push,
    info: shorthand('info'),
    success: shorthand('success'),
    warning: shorthand('warning'),
    danger: shorthand('danger'),
    dismiss,
    clear,
    pause,
    resume,
  }
}
